import { useReducedMotion } from 'framer-motion';
import type { FC, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useMemoOne } from 'use-memo-one';
import { useStorybook } from 'src/hooks/use-storybook';
import { assertContext } from 'src/utils/assert-context';

export const stickyHeaderId = 'sticky-header';

type ScrollToOptions = {
  behaviour?: ScrollBehavior;
  offset?: number;
};

type ScrollManagerContextValue = {
  check: () => void;
  scrollTo: (
    hash: string,
    behaviourOrOptions?: ScrollBehavior | ScrollToOptions
  ) => void;
};

// pass undefined as any, because we run assertContext at runtime
const ScrollManagerContext = createContext<ScrollManagerContextValue>(
  undefined as any
);

export function useScrollManager() {
  const context = useContext(ScrollManagerContext);
  assertContext(context, 'ScrollManager');
  return context;
}

export const ScrollManagerProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { pathname, hash } = useLocation();
  const { isDocsPage } = useStorybook();
  const reducedMotion = useReducedMotion();

  // note: checkedHash is an object instead of a string, because
  // we might want to scroll to the same area multiple times
  // without changing the hash in between
  const [checkedHash, setCheckedHash] = useState<{
    value: string;
    behaviour?: ScrollBehavior;
    offset?: number;
  }>({ value: '' });

  // useLayoutEffect, because resetting the checkedHash happened _after_
  // check was called (e.g. in Settings > Registration) with useEffect
  useLayoutEffect(() => {
    // forget checkedHash, because we switched to a new page
    // (which might uses the same hash)
    setCheckedHash({ value: '' });

    // disable scrolling in storybook docs views
    // (or you would jump to the start of the docs page, when you
    // interact with the examples)
    if (isDocsPage) return;

    window.scroll(0, 0);
  }, [pathname, isDocsPage]);

  useEffect(() => {
    if (!checkedHash.value) return;

    const targetId = checkedHash.value.substr(1);

    const target = document.getElementById(targetId);
    const header = document.getElementById(stickyHeaderId);

    if (target && header) {
      window.scrollTo({
        top:
          target.getBoundingClientRect().top +
          window.pageYOffset -
          header.clientHeight +
          (checkedHash.offset ?? 0),
        left: 0,
        behavior: checkedHash.behaviour,
      });
    } else {
      // Log warnings only during development to find typos in ids
      if (process.env.NODE_ENV !== 'production') {
        if (!target) {
          console.warn(
            `ScrollManagerProvider: Could not find element #${targetId}`
          );
        }
        if (!header) {
          console.warn(
            `ScrollManagerProvider: Could not find element #${stickyHeaderId}`
          );
        }
      }
      // If either element is missing, scroll to top as a fallback
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: checkedHash.behaviour,
      });
    }
  }, [checkedHash]);

  const check = useCallback(() => setCheckedHash({ value: hash }), [hash]);

  const scrollTo = useCallback(
    (hash: string, behaviourOrOptions?: ScrollBehavior | ScrollToOptions) => {
      const defaultBehaviour = reducedMotion ? 'instant' : 'smooth';
      const options =
        typeof behaviourOrOptions === 'string'
          ? { behaviour: behaviourOrOptions }
          : (behaviourOrOptions ?? {});
      setCheckedHash({
        value: hash,
        behaviour: options.behaviour ?? defaultBehaviour,
        offset: options.offset,
      });
    },
    [reducedMotion]
  );

  const value = useMemoOne(() => ({ check, scrollTo }), [check, scrollTo]);

  return (
    <ScrollManagerContext.Provider value={value}>
      {children}
    </ScrollManagerContext.Provider>
  );
};

/**
 * Use this component to indicate that the page has loaded and the anchor you want to use
 * is available. Use this for navigations within one page.
 *
 * ```
 * const { hash } = useLocation();
 * <ScrollCheck key={hash} />
 * ```
 */
export const ScrollCheck = () => {
  const { check } = useScrollManager();

  // oxlint-disable-next-line exhaustive-deps
  useEffect(check, []);

  return null;
};
